import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Chip } from '../aa';
import type { ChipTone } from '../aa';
import './intake-rail.css';

/*
 * One section of an intake, as a rail of cards that slides sideways.
 *
 * A section can hold thirty questions, so the rail pages rather than stacks:
 * several are on screen at once and the arrows push the track left, which is
 * the only way an editor can still scan a section they are halfway through.
 *
 * The scroll position is the state. Nothing here tracks a page number, because
 * a trackpad swipe moves the same track and a counter would go out of step
 * with what is actually on screen.
 */

/* Kept in step with the flex-basis in intake-rail.css. */
const CARD_WIDTH = 264;

export const IntakeRail: React.FC<{
  /** The section's own header row. The arrows are appended to it. */
  head: React.ReactNode;
  children: React.ReactNode;
}> = ({ head, children }) => {
  const track = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const measure = useCallback(() => {
    const el = track.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  /* Hiding a question shortens the track under a scroll position that was
     valid a moment ago, so the arrows are re-measured whenever the cards
     change and not only when the window does. */
  useEffect(() => { measure(); }, [measure, children]);

  /* A page is the rail minus one card's worth, so the card you were reading
     at the edge is still on screen after the slide and there is no seam. */
  const slide = (direction: -1 | 1) => {
    const el = track.current;
    if (!el) return;
    let behavior: ScrollBehavior = 'smooth';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'auto';
    el.scrollBy({ left: direction * Math.max(CARD_WIDTH, el.clientWidth - CARD_WIDTH), behavior });
  };

  const scrollable = !atStart || !atEnd;

  return (
    <div className="mb-5">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        {head}
        {scrollable && (
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => slide(-1)}
              disabled={atStart}
              aria-label="Earlier questions"
              className="rounded-md border border-line p-1 text-ink-muted hover:bg-canvas-inset hover:text-ink disabled:opacity-35 disabled:hover:bg-transparent"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              type="button"
              onClick={() => slide(1)}
              disabled={atEnd}
              aria-label="Later questions"
              className="rounded-md border border-line p-1 text-ink-muted hover:bg-canvas-inset hover:text-ink disabled:opacity-35 disabled:hover:bg-transparent"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        )}
      </div>
      <div ref={track} className="intake-rail-track" onScroll={measure}>
        {children}
      </div>
    </div>
  );
};

export const IntakeCard: React.FC<{
  rank: number;
  label: string;
  /** The key and kind. Left out on the opening questions, which are the least
      technical screen in the product and name nothing a partner did not type. */
  meta?: string;
  why: string | null;
  critical: boolean;
  hidden: boolean;
  badge: { text: string; tone: ChipTone };
  actions: React.ReactNode;
}> = ({ rank, label, meta = '', why, critical, hidden, badge, actions }) => {
  let cls = 'flex min-h-[188px] flex-col gap-1.5 rounded-card border border-line bg-white p-4';
  if (hidden) cls += ' opacity-50';
  return (
    <div className={cls}>
      {/* The section is named once, in the header above the rail. Repeating it
          on every card of that rail crowded out the question. */}
      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-soft">
        {String(rank).padStart(2, '0')}
      </span>
      <div className="flex items-start gap-1.5">
        <strong className="text-[14px] font-semibold leading-[1.35] text-ink">{label}</strong>
        {critical && <Star size={12} className="mt-1 shrink-0 text-danger" aria-label="Critical" />}
      </div>
      {meta && <div className="truncate font-mono text-[11px] text-ink-soft">{meta}</div>}
      {why && <small className="line-clamp-3 text-[12px] leading-[18px] text-ink-dim">{why}</small>}
      <div className="mt-auto flex items-center justify-between gap-2 pt-2">
        <Chip tone={badge.tone}>{badge.text}</Chip>
        {actions}
      </div>
    </div>
  );
};

export default IntakeRail;
